// Shared chat attachment UI — composer (attach/paste/drag-drop + progress) and message
// rendering (inline images with lightbox, inline video, file cards). Used by WorkerMessages
// and AdminMessages so both conversations behave identically.
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, Loader2, RefreshCw, FileText, Download, AlertCircle } from "lucide-react";
import { CHAT_ACCEPT, chatFileUrl, attachmentKind, fmtFileSize, uploadChatFile } from "@/utils/chatAttachments";

export const useChatUploads = () => {
  const [pending, setPending] = useState([]);

  const addFiles = useCallback((files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const entries = list.map((file) => ({
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      name: file.name || "pasted-image.png",
      size: file.size,
      progress: 0,
      status: "uploading",
      meta: null,
      previewUrl: (file.type || "").startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setPending((p) => [...p, ...entries]);
    entries.forEach((entry) => {
      uploadChatFile(entry.file, (pct) =>
        setPending((p) => p.map((x) => (x.key === entry.key ? { ...x, progress: pct } : x)))
      )
        .then((meta) => setPending((p) => p.map((x) => (x.key === entry.key ? { ...x, status: "done", meta, progress: 100 } : x))))
        .catch((err) => {
          const d = err.response?.data?.detail;
          toast.error(typeof d === "string" ? d : `Could not upload ${entry.name}.`);
          setPending((p) => p.map((x) => (x.key === entry.key ? { ...x, status: "error" } : x)));
        });
    });
  }, []);

  const retry = useCallback((key) => {
    setPending((p) => {
      const entry = p.find((x) => x.key === key);
      if (!entry) return p;
      uploadChatFile(entry.file, (pct) =>
        setPending((q) => q.map((x) => (x.key === key ? { ...x, progress: pct } : x)))
      )
        .then((meta) => setPending((q) => q.map((x) => (x.key === key ? { ...x, status: "done", meta, progress: 100 } : x))))
        .catch(() => setPending((q) => q.map((x) => (x.key === key ? { ...x, status: "error" } : x))));
      return p.map((x) => (x.key === key ? { ...x, status: "uploading", progress: 0 } : x));
    });
  }, []);

  const remove = useCallback((key) => setPending((p) => p.filter((x) => x.key !== key)), []);
  const clear = useCallback(() => setPending([]), []);
  const uploading = pending.some((x) => x.status === "uploading");
  const failed = pending.some((x) => x.status === "error");
  const metas = pending.filter((x) => x.status === "done").map((x) => x.meta);
  return { pending, addFiles, retry, remove, clear, uploading, failed, metas };
};

export const AttachButton = ({ onFiles, disabled }) => {
  const inputRef = useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={CHAT_ACCEPT}
        className="hidden"
        data-testid="chat-file-input"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
      />
      <button
        type="button"
        data-testid="chat-attach-btn"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--pv-border)] text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-50"
        aria-label="Attach files"
      >
        <Paperclip className="h-4 w-4" />
      </button>
    </>
  );
};

export const PendingAttachments = ({ pending, onRetry, onRemove }) => {
  if (!pending.length) return null;
  return (
    <div data-testid="pending-attachments" className="mb-2 flex flex-wrap gap-2">
      {pending.map((x) => (
        <div key={x.key} data-testid={`pending-${x.key}`} className="relative flex items-center gap-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-raised)] px-3 py-2">
          {x.previewUrl ? (
            <img src={x.previewUrl} alt="" className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <FileText className="h-4 w-4 shrink-0 text-cyan-400" />
          )}
          <div className="min-w-0">
            <p className="max-w-[140px] truncate text-[11px] font-bold text-[var(--pv-text)]">{x.name}</p>
            <p className="text-[10px] text-[var(--pv-text-4)]">
              {x.status === "uploading" && `Uploading ${x.progress}%`}
              {x.status === "done" && fmtFileSize(x.size)}
              {x.status === "error" && <span className="font-bold text-rose-400">Failed</span>}
            </p>
          </div>
          {x.status === "uploading" && <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-400" />}
          {x.status === "error" && (
            <button type="button" data-testid={`retry-${x.key}`} onClick={() => onRetry(x.key)} className="text-amber-400 hover:text-amber-300" aria-label="Retry upload">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
          <button type="button" data-testid={`remove-${x.key}`} onClick={() => onRemove(x.key)} className="text-[var(--pv-text-4)] hover:text-rose-400" aria-label="Remove attachment">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

export const AttachmentList = ({ attachments, mine }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!attachments || !attachments.length) return null;
  return (
    <>
      <div className={`space-y-2 ${attachments.length ? "mb-1.5" : ""}`}>
        {attachments.map((a) => {
          const kind = attachmentKind(a.content_type);
          const url = chatFileUrl(a.id);
          if (kind === "image") {
            return (
              <button key={a.id} type="button" data-testid={`attachment-image-${a.id}`} onClick={() => setLightbox(a)} className="block overflow-hidden rounded-xl border border-white/10">
                <img src={url} alt={a.filename} loading="lazy" className="max-h-56 max-w-full object-cover" />
              </button>
            );
          }
          if (kind === "video") {
            return (
              <video key={a.id} data-testid={`attachment-video-${a.id}`} controls preload="metadata" src={url} className="max-h-64 max-w-full rounded-xl border border-white/10" />
            );
          }
          return (
            <a
              key={a.id}
              data-testid={`attachment-file-${a.id}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 transition-colors ${mine ? "border-white/20 bg-white/10 hover:bg-white/15" : "border-[var(--pv-border)] bg-[var(--pv-bg)] hover:border-cyan-500/30"}`}
            >
              <FileText className={`h-4 w-4 shrink-0 ${mine ? "text-white" : "text-cyan-400"}`} />
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-xs font-bold ${mine ? "text-white" : "text-[var(--pv-text)]"}`}>{a.filename}</span>
                <span className={`text-[10px] ${mine ? "text-cyan-100" : "text-[var(--pv-text-4)]"}`}>
                  {(a.content_type || "file").split("/").pop().toUpperCase()} · {fmtFileSize(a.size)}
                </span>
              </span>
              <Download className={`h-3.5 w-3.5 shrink-0 ${mine ? "text-white" : "text-[var(--pv-text-4)]"}`} />
            </a>
          );
        })}
      </div>
      {lightbox && (
        <div data-testid="attachment-lightbox" className="fixed inset-0 z-[130] flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)}>
          <img src={chatFileUrl(lightbox.id)} alt={lightbox.filename} className="max-h-full max-w-full rounded-xl object-contain" />
        </div>
      )}
    </>
  );
};

// drop + paste wiring for a chat container
export const useChatDropPaste = (addFiles) => {
  const [dragging, setDragging] = useState(false);
  const dragProps = {
    onDragOver: (e) => { e.preventDefault(); setDragging(true); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); },
    onDrop: (e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); },
  };
  const onPaste = useCallback((e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);
  return { dragging, dragProps, onPaste };
};

export const DragOverlay = ({ visible }) =>
  visible ? (
    <div data-testid="chat-drag-overlay" className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-3xl border-2 border-dashed border-cyan-400 bg-cyan-500/10">
      <p className="flex items-center gap-2 text-sm font-bold text-cyan-300"><AlertCircle className="h-4 w-4" /> Drop files to attach</p>
    </div>
  ) : null;
